import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import extrasApi from "@/api/extrasApi";
import ExtraResponse from "@/model/ExtraResponse";
import formatPrice from "@/utils/formatPrice";
import "@/pages/ExtrasPage/ExtrasPage.scss";

const PAGE_SIZE = 20;

function ExtrasPage() {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [extras, setExtras] = useState<ExtraResponse[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    extrasApi
      .listExtras(session.accessToken, page, PAGE_SIZE)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setExtras(result.content ?? []);
        setTotalPages(result.totalPages ?? 0);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session, page]);

  if (!session) {
    return null;
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(t("extras.deleteConfirm"))) {
      return;
    }

    setDeletingId(id);
    setDeleteError(false);

    try {
      await extrasApi.deleteExtra(session.accessToken, id);
      setExtras((current) => current.filter((extra) => extra.id !== id));
    } catch {
      setDeleteError(true);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="extras-page">
      <header className="extras-page__header">
        <div>
          <h1>{t("extras.title")}</h1>
          <p className="extras-page__subtitle">{t("extras.subtitle")}</p>
        </div>
        <Link to="/extras/new">{t("extras.createButton")}</Link>
      </header>

      {deleteError && (
        <p className="extras-page__error">{t("extras.deleteError")}</p>
      )}

      {isLoading && <p>{t("extras.loading")}</p>}
      {!isLoading && error && <p role="alert">{t("extras.error")}</p>}
      {!isLoading && !error && extras.length === 0 && <p>{t("extras.empty")}</p>}

      {!isLoading && !error && extras.length > 0 && (
        <>
          <table className="extras-page__table">
            <thead>
              <tr>
                <th>{t("extras.columns.name")}</th>
                <th>{t("extras.columns.description")}</th>
                <th>{t("extras.columns.price")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {extras.map((extra, index) => (
                <tr key={extra.id ?? index}>
                  <td>{extra.name ?? "—"}</td>
                  <td>{extra.description ?? "—"}</td>
                  <td>
                    {formatPrice(extra.priceAmount, extra.priceCurrency)}{" "}
                    {extra.pricingUnit && (
                      <span className="extras-page__unit">
                        {t(`extras.pricingUnit.${extra.pricingUnit}`)}
                      </span>
                    )}
                  </td>
                  <td className="extras-page__actions">
                    {extra.id && (
                      <>
                        <Link to={`/extras/${extra.id}/edit`}>
                          {t("extras.editButton")}
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(extra.id as string)}
                          disabled={deletingId === extra.id}
                        >
                          {deletingId === extra.id
                            ? t("extras.deleting")
                            : t("extras.deleteButton")}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="extras-page__pagination">
            <button
              type="button"
              onClick={() => setPage((current) => current - 1)}
              disabled={page <= 0}
            >
              {t("pagination.previous")}
            </button>
            <span>
              {t("pagination.pageInfo", { page: page + 1, totalPages })}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => current + 1)}
              disabled={page + 1 >= totalPages}
            >
              {t("pagination.next")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ExtrasPage;
